import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, insertWithUserId } from "@/lib/hooks/api/utils";
import type { Contact_NotesUpdateInput } from "@/src/gql/graphql";
import type { Database } from "@/lib/supabase/database.types";
import { Contact_NotesUpdateInputSchema } from "@/src/gql/validation";

export function useCreateContactNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteData: {
      content: string;
      contact_id: string;
      title?: string;
      /** Codegen-derived: `contact_notes.note_type` is a Postgres enum, not free text. */
      note_type?: Database["public"]["Enums"]["note_type"];
      tags?: string[];
      is_internal?: boolean;
    }) => {
      const noteDataWithDefaults = {
        ...noteData,
        note_type: noteData.note_type || "general",
        is_internal: noteData.is_internal || false,
      };

      const { data, error } = await insertWithUserId("contact_notes", noteDataWithDefaults);

      if (error) {
        console.error("Error creating contact note:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-notes"] });
      toast.success("Note created successfully");
    },
    onError: (error) => {
      toast.error("Failed to create note");
      console.error("Error creating contact note:", error);
    },
  });
}

export function useUpdateContactNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Contact_NotesUpdateInput }) => {
      const validatedData = Contact_NotesUpdateInputSchema.parse(updates);

      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_notes")
        .update(validatedData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating contact note:", error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["contact-notes"] });
      void queryClient.invalidateQueries({ queryKey: ["contact-notes", data.id] });
      toast.success("Note updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update note");
      console.error("Error updating contact note:", error);
    },
  });
}

export function useDeleteContactNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const supabase = getSupabase();
      const { error } = await supabase.from("contact_notes").delete().eq("id", id);

      if (error) {
        console.error("Error deleting contact note:", error);
        throw new Error(error.message);
      }

      return { id, deleted: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact-notes"] });
      toast.success("Note deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete note");
      console.error("Error deleting contact note:", error);
    },
  });
}
